/**
 * GuidedFlow + Stepper — Non-HR UX layer (HUMANAI_UX_NONHR.md §3, หลักการ 2).
 *
 * "นำทาง ไม่ใช่ให้เลือกเอง". ออกแบบเผื่อผู้ใช้ลืมวิธีระหว่างรอบ (ปิดเงินเดือน
 * เดือนละครั้ง — ลืมจริง) → พาเดินทีละ step ทุกครั้งเหมือนครั้งแรก.
 *
 *   <Stepper>      — แถบบอกขั้น (presentational). กดข้ามได้ถ้าส่ง onStepClick.
 *                    เป็นรุ่น generic ของ PayrollPipeline เดิม — reuse ทุก flow.
 *   <GuidedFlow>   — controller จัด state step + ปุ่มย้อน/ถัดไป สำหรับ wizard
 *                    ในตัว (เช่น เพิ่มพนักงานใหม่). flow ที่กระจายหลายหน้า
 *                    (เช่น รอบเงินเดือน) ใช้ <Stepper> เปล่า + routing เดิมพอ.
 *
 * Portable — CSS class กลาง + lucide เท่านั้น (reuse ข้าม product).
 */
import { useState, type ReactNode } from 'react';
import { Check } from 'lucide-react';

export interface FlowStep {
  /** key เสถียรของขั้น */
  id: string;
  /** label ภาษาคนสั้นๆ */
  label: string;
}

/** แถบขั้นตอน — done = ติ๊กเขียว, active = วงแหวน, ที่เหลือ = เทา */
export function Stepper({
  steps,
  current,
  onStepClick,
}: {
  steps: FlowStep[];
  /** index ของขั้นปัจจุบัน */
  current: number;
  /** ถ้าส่งมา = กดขั้นที่ทำผ่านแล้ว/ปัจจุบันเพื่อกระโดดได้ */
  onStepClick?: (index: number, step: FlowStep) => void;
}) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto py-1">
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        const clickable = !!onStepClick && i <= current;
        const dot = (
          <span
            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition ${
              active
                ? 'bg-brand-600 text-white ring-2 ring-brand-200'
                : done
                  ? 'bg-brand-600 text-white'
                  : 'bg-gray-200 text-gray-500'
            }`}
          >
            {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
          </span>
        );
        const label = (
          <span className={`text-xs whitespace-nowrap ${active ? 'text-gray-900 font-semibold' : done ? 'text-gray-700' : 'text-gray-400'}`}>
            {s.label}
          </span>
        );
        return (
          <div key={s.id} className="flex items-center gap-1 shrink-0">
            {clickable ? (
              <button type="button" onClick={() => onStepClick!(i, s)} className="flex items-center gap-1.5 group hover:opacity-80">
                {dot}
                {label}
              </button>
            ) : (
              <div className="flex items-center gap-1.5">
                {dot}
                {label}
              </div>
            )}
            {i < steps.length - 1 && <div className={`w-4 h-0.5 ${done ? 'bg-brand-400' : 'bg-gray-200'}`} />}
          </div>
        );
      })}
    </div>
  );
}

/**
 * GuidedFlow — wizard ในหน้าเดียว จัด state ขั้นเอง + ปุ่มนำทางล่าง.
 * `render(step, helpers)` คืน content ของขั้นปัจจุบัน. helpers มี next/back/goTo.
 * `canNext` คุมว่าขั้นนี้ไปต่อได้ไหม (validate). `onFinish` ยิงตอนกดขั้นสุดท้าย.
 */
export function GuidedFlow({
  steps,
  render,
  onFinish,
  canNext,
  finishLabel = 'เสร็จสิ้น',
  nextLabel = 'ถัดไป',
  backLabel = 'ย้อนกลับ',
}: {
  steps: FlowStep[];
  render: (ctx: { index: number; step: FlowStep; next: () => void; back: () => void; goTo: (i: number) => void }) => ReactNode;
  onFinish?: () => void;
  canNext?: (index: number) => boolean;
  finishLabel?: string;
  nextLabel?: string;
  backLabel?: string;
}) {
  const [index, setIndex] = useState(0);
  const last = steps.length - 1;
  const goTo = (i: number) => setIndex(Math.max(0, Math.min(last, i)));
  const next = () => (index < last ? goTo(index + 1) : onFinish?.());
  const back = () => goTo(index - 1);
  const blocked = canNext ? !canNext(index) : false;

  return (
    <div className="space-y-5">
      <Stepper steps={steps} current={index} onStepClick={(i) => i < index && goTo(i)} />
      <div>{render({ index, step: steps[index]!, next, back, goTo })}</div>
      <div className="flex gap-2">
        {index > 0 && (
          <button type="button" className="btn-secondary" onClick={back}>
            {backLabel}
          </button>
        )}
        <button type="button" className="btn-cta flex-1" onClick={next} disabled={blocked}>
          {index < last ? nextLabel : finishLabel}
        </button>
      </div>
    </div>
  );
}
